import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EiqButton } from 'eiq-storybook';
import { CAP_IMPLEMENTATION }  from 'src/utils/data/job'
import { CapAction } from 'src/components/CapActionNew/CapAction';
import CapEditTextAreaField from 'src/components/CapFieldsNew/CapEditTextAreaField';
import { FINDING_STATUS, SEVERITY, REVIEW_METHOD, getCapFinding, hideAddAction } from './utils';
import s from './CapFinding.styles';

export const CapFinding = ({ finding, index, editableForExternalUser, setFieldValue, errors, findings, originalFinding, isCapManager, isAdmin, setIsDirty, currentUserId, jobStatus, overallCapStatus, capType }) => {
  const { t } = useTranslation();
  const findingInfo = getCapFinding(originalFinding);
  const { findingStatus, severity } = findingInfo;

  const [collapsed, setCollapsed] = useState(true);

  const handleBlur = (fieldName, val) => {
    const updatedFindings = findings.length ? [...findings] : [];
    updatedFindings[index][fieldName] = val;
    updatedFindings[index].modified = true;
    
    setFieldValue('findings', updatedFindings);
    setIsDirty(true);
  }

  const handleAddAction = async () => {
    const newActions = [...(finding.actions || [])];
    const initialActionStatus = (overallCapStatus === 'cap_remediation_underway' &&  capType === 'desktop_evidence_review' && jobStatus === CAP_IMPLEMENTATION	) ? 'open_action_pending' : 'review_pending'
    newActions.push({
      dueDate: null,
      actualCompletionOfActionDate: null,
      responsiblePerson: '',
      description: '',
      comments: [],
      attachmentIds: [],
      attachmentsMeta: [],
      status: initialActionStatus ,
      resources: [],
      modified: true
    });

    const updatedFinding = { ...finding };
    updatedFinding.actions = newActions;

    const updatedFindings = [ ...findings ];
    updatedFindings[index] = updatedFinding;
    updatedFindings[index].modified = true;

    setFieldValue('findings', updatedFindings);
    setIsDirty(true);
  };

  const setFindingsValue = async (updatedFinding) => {
    const updatedFindings = findings ? [...findings] : [];
    updatedFindings[index] = updatedFinding;

    setFieldValue('findings', updatedFindings);
    setIsDirty(true);
  };

  const getOriginalAction = (actionId) => {
    return originalFinding.actions.find(action => action.id === actionId) || {};
  };

  const actionRequiredError = errors?.actions && (typeof(errors.actions) === 'string') && errors.actions;

  return (
    <s.ImprovementSection>
      <s.ImprovementHeader>
        <s.Heading className="pl-15">{t('AREA_OF_IMPROVEMENT')} {index + 1} </s.Heading>
        <s.CollapsableWrapper className="pr-15 collapsable"
                              type="button"
                              onClick={() => setCollapsed(!collapsed)}
                              data-testid={`collapsable-button-${index}`} >
          {collapsed ? 
            <s.CollapseIcon name="collapseIcon" className="cursor-pointer" /> : 
            <s.ExpandIcon name="expandIcon" className="cursor-pointer" />
          }
        </s.CollapsableWrapper>
      </s.ImprovementHeader>
      <s.InfoWrapper>
        <s.LeftSection className="pl-15">
          <s.SeverityDiv className={SEVERITY[severity]?.class || ''}>
            {t(SEVERITY[severity]?.text)}
          </s.SeverityDiv>
          <s.List>
            <s.ListItem>
              <s.Label>{findingInfo.category}</s.Label>
              <s.Content>{findingInfo.categoryContent}</s.Content>
            </s.ListItem>
            <s.ListItem>
              <s.Label>{t('COLUMN_ACTION')}</s.Label>
              <s.Content>{findingInfo.actionCount}</s.Content>
            </s.ListItem>
            <s.ListItem>
              <s.Label>{t('REVIEW_METHOD')}</s.Label>
              <s.Content>{t(REVIEW_METHOD[findingInfo.reviewMethod]) || '-'}</s.Content>
            </s.ListItem>
            <s.ListItem>
              <s.Label>{t('DUE_DATE')}</s.Label>
              <s.Content>{findingInfo?.maxDueDate}</s.Content>
            </s.ListItem>
          </s.List>
        </s.LeftSection>
        <div className="pr-15 pt-9">
          <s.FindingStatus className={FINDING_STATUS[findingStatus]?.class || ''}>
            {t(FINDING_STATUS[findingStatus]?.text)}
          </s.FindingStatus>
        </div>
      </s.InfoWrapper>
      <s.DetailsWrapper className=''>
        <>
          <s.DetailedFindingText className='pl-15 pr-15'>{findingInfo?.detailedFinding}</s.DetailedFindingText>
          <s.ToggleWrapper > 
            <div className={`collapsable ${!collapsed ? 'expanded' : ''}`} data-testid={`collapsable-${index}`}>
              <s.DetailWrapper className='pl-15 pr-15'>
                <s.Heading>{t('CAP_LEGAL_OF_COC_REQUIREMENTS')}</s.Heading>
                <s.Text>{findingInfo?.legalRequirement}</s.Text>
              </s.DetailWrapper>
              <s.DetailWrapper className='pl-15 pr-15'>
                <s.Heading>{t('RECOMMENDATION')}</s.Heading>
                <s.Text>{findingInfo?.recommendations}</s.Text>
              </s.DetailWrapper>
              <s.DetailWrapper>
                <s.Heading className='pl-15 pr-15'>{t('CAP_ROOT_CAUSE')}</s.Heading>
                <CapEditTextAreaField
                  isEditable={editableForExternalUser}
                  name="rootCause"
                  wrapperClassName="editableField"
                  value={findingInfo.rootCause}
                  rows={1}
                  onBlur={(val) => handleBlur('rootCause', val)}
                  hasLabel={true}
                  required
                />
              </s.DetailWrapper>
            </div>      
          </s.ToggleWrapper>
        </>
      </s.DetailsWrapper>
      {editableForExternalUser && !hideAddAction(findingStatus, jobStatus, isCapManager, capType) && <s.ButtonWrapper className="pl-15" >
          <EiqButton variant='btn--outline-link' label={`+ ${t('CAP_ADD_ACTION')}`} onClick={handleAddAction}/>
        </s.ButtonWrapper>
      }
      { !!actionRequiredError && <s.ErrorWrapper>{actionRequiredError}</s.ErrorWrapper> }
      { (finding?.actions || []).map((action, i) => (
        <CapAction key={i}
                   index={i}
                   action={action}
                   editableForExternalUser={editableForExternalUser}
                   setFieldValue={setFieldValue}
                   errors={errors?.actions && errors?.actions[i]}
                   actions={finding.actions}
                   setFindingsValue={setFindingsValue}
                   finding={finding}
                   originalAction={getOriginalAction(action.id)}
                   isCapManager={isCapManager}
                   isAdmin={isAdmin}
                   setIsDirty={setIsDirty}
                   reviewMethod={findingInfo.reviewMethod}
                   currentUserId={currentUserId}
                   overallCapStatus={overallCapStatus}
                   capType={capType}
        />
      ))}
    </s.ImprovementSection>
  );
};